import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CitedMarkdown from '../../../components/markdown/CitedMarkdown';
import { splitTrimmed } from '../../../utils/text';

const NO_FOLD = Number.MAX_SAFE_INTEGER;

const TABS = [
  { id: 'glance', label: 'Event at a glance' },
  { id: 'full', label: 'Full report' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'analysis', label: 'Agreements & differences' },
  { id: 'takeaways', label: 'Takeaways' },
];

const MD_LINK = /\[([^[\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

/** Split the trailing "Session(s): [title](url), ..." line off a text field. */
const splitSessions = (text) => {
  const match = /\n*\s*Sessions?:\s*(.+)$/s.exec(text || '');
  if (!match || !MD_LINK.test(match[1])) return { body: text || '', links: [] };
  MD_LINK.lastIndex = 0;
  const links = [...match[1].matchAll(MD_LINK)].map((m) => ({ title: m[1], url: m[2] }));
  return { body: (text || '').slice(0, match.index).trim(), links };
};

const parseRoles = (markdown) => {
  const roles = {};
  const re = /- \*\*(.+?)\*\*\s*\n\s+- Role\/Title:\s*(.+)/g;
  let m;
  while ((m = re.exec(markdown || '')) !== null) roles[m[1]] = m[2].trim();
  return roles;
};

const EventSynthesisOutput = ({ output, onCitationClick }) => {
  const [tab, setTab] = useState('glance');
  const [filter, setFilter] = useState('');
  const [openSpeakers, setOpenSpeakers] = useState({});

  const synthesis = useMemo(() => output?.synthesis || {}, [output]);
  const glance = synthesis['Session report']?.['Session at a glance'] || {};
  const fullReport = synthesis['Session report']?.['Full session report'] || '';
  const speakers = useMemo(() => synthesis.Speakers || {}, [synthesis]);
  const analysis = synthesis['Additional analysis'] || {};
  const transcriptReferences = output?.transcript_references || {};
  const sourceReferences = output?.source_references || {};
  const roles = useMemo(() => parseRoles(glance.Speakers), [glance.Speakers]);

  const speakerNames = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return Object.keys(speakers).filter((name) => (
      !needle
      || name.toLowerCase().includes(needle)
      || (roles[name] || '').toLowerCase().includes(needle)
    ));
  }, [speakers, roles, filter]);

  if (!output?.synthesis) return null;

  const md = (text, key, className = 'custom-report-body') => (
    <CitedMarkdown
      key={key}
      text={text || ''}
      maxLength={NO_FOLD}
      className={className}
      transcriptReferences={transcriptReferences}
      sourceReferences={sourceReferences}
      onCitationClick={onCitationClick}
    />
  );

  const withSessions = (text, key) => {
    const { body, links } = splitSessions(text);
    return (
      <>
        {md(body, key)}
        {links.length > 0 && (
          <p className="event-synthesis-sessions">
            {links.length === 1 ? 'Session: ' : 'Sessions: '}
            {links.map((link, i) => (
              <React.Fragment key={link.url}>
                {i > 0 && ', '}
                <a href={link.url} target="_blank" rel="noreferrer">{link.title}</a>
              </React.Fragment>
            ))}
          </p>
        )}
      </>
    );
  };

  const speakerChips = (names) => (
    names?.length > 0 && (
      <div className="event-synthesis-chips">
        {names.map((name) => <span key={name}>{name}</span>)}
      </div>
    )
  );

  const stats = output.stats || {};
  const agreements = analysis.Agreements || {};
  const differences = analysis.Differences || {};
  const takeaways = analysis.Takeaways || {};
  const followUps = Object.keys(analysis['Follow-up questions'] || {});

  return (
    <div className="event-synthesis">
      <h2 className="event-synthesis-title">{output.report_title}</h2>
      <p className="event-synthesis-meta">
        {stats.sessions} sessions · {stats.speakers} speakers · {stats.arguments} arguments · {stats.themes} themes
      </p>

      <div className="event-synthesis-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`event-synthesis-tab${tab === item.id ? ' active' : ''}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'glance' && (
        <div className="event-synthesis-panel">
          <h3>Summary</h3>
          {md(glance.Summary, 'summary')}
          <h3>Key points</h3>
          {md(glance.Keypoints, 'keypoints')}
          <h3>Sessions</h3>
          <ul className="event-synthesis-list">
            {(output.sources || []).map((s) => (
              <li key={s.session_id}>
                {s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.session_title}</a> : s.session_title}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'full' && <div className="event-synthesis-panel">{md(fullReport, 'full')}</div>}

      {tab === 'speakers' && (
        <div className="event-synthesis-panel">
          <input
            type="search"
            className="custom-reports-search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Filter ${Object.keys(speakers).length} speakers...`}
          />
          {speakerNames.map((name) => {
            const args = speakers[name] || {};
            const open = Boolean(openSpeakers[name]);
            return (
              <div key={name} className="event-synthesis-speaker">
                <button
                  type="button"
                  className="event-synthesis-speaker-head"
                  onClick={() => setOpenSpeakers((prev) => ({ ...prev, [name]: !prev[name] }))}
                  aria-expanded={open}
                >
                  {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  <strong>{name}</strong>
                  {roles[name] && <span className="event-synthesis-role">{roles[name]}</span>}
                  <em>{Object.keys(args).length}</em>
                </button>
                {open && Object.entries(args).map(([argument, detail]) => (
                  <div key={argument} className="event-synthesis-argument">
                    <h4>{argument}</h4>
                    {detail['Major discussion point'] && (
                      <span className="event-synthesis-theme">{detail['Major discussion point']}</span>
                    )}
                    {md(detail.Explanation, 'explanation')}
                    {detail.Evidence && withSessions(detail.Evidence, 'evidence')}
                    {detail.External_evidence && md(detail.External_evidence, 'external')}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'analysis' && (
        <div className="event-synthesis-panel">
          <h3>Agreement points</h3>
          {Object.entries(agreements['Agreement points'] || {}).map(([point, item]) => (
            <div key={point} className="event-synthesis-item">
              <h4>{point}</h4>
              <span className="event-synthesis-theme">{item.Topics}</span>
              {withSessions(item.Summary, 'summary')}
              {speakerChips(item.Speakers)}
            </div>
          ))}
          <h3>Similar viewpoints</h3>
          {Object.entries(agreements['Similar viewpoints'] || {}).map(([point, item]) => (
            <div key={point} className="event-synthesis-item">
              <h4>{point}</h4>
              <span className="event-synthesis-theme">{item.Topics}</span>
              {withSessions(item.Arguments, 'arguments')}
              {speakerChips(item.Speakers)}
            </div>
          ))}
          <h3>Different viewpoints</h3>
          {Object.entries(differences['Different viewpoints'] || {}).map(([point, item]) => (
            <div key={point} className="event-synthesis-item">
              <h4>{point}</h4>
              <span className="event-synthesis-theme">{item.Topics}</span>
              <ul className="event-synthesis-list">
                {splitTrimmed(item.Arguments).map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          ))}
          <h3>Overall assessment</h3>
          {md(agreements['Overall assessment']?.Summary, 'agree-summary')}
          {agreements['Overall assessment']?.['Consensus level'] && (
            <p className="event-synthesis-level">
              <strong>Consensus level:</strong> {agreements['Overall assessment']['Consensus level']}
            </p>
          )}
          {md(differences['Overall assessment']?.Summary, 'diff-summary')}
          {differences['Overall assessment']?.['Disagreement level'] && (
            <p className="event-synthesis-level">
              <strong>Disagreement level:</strong> {differences['Overall assessment']['Disagreement level']}
            </p>
          )}
        </div>
      )}

      {tab === 'takeaways' && (
        <div className="event-synthesis-panel">
          {Object.entries(takeaways).map(([heading, value]) => (
            splitTrimmed(value).length > 0 && (
              <React.Fragment key={heading}>
                <h3>{heading}</h3>
                <ul className="event-synthesis-list">
                  {splitTrimmed(value).map((line) => <li key={line}>{md(line, 'line', 'custom-report-inline')}</li>)}
                </ul>
              </React.Fragment>
            )
          ))}
          {followUps.length > 0 && (
            <>
              <h3>Follow-up questions</h3>
              <ul className="event-synthesis-list">
                {followUps.map((q) => <li key={q}>{q}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EventSynthesisOutput;
